import React, { useState, useMemo, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { POWER_PLANTS } from "../../../constants/powerPlants";
import { PowerPlant, PowerPlantType } from "../../../types/powerPlants";
import { formatCurrency, formatNumber } from "../../../utils/formatting";

import { calculateBreakawayPowerRequirements } from "../../../utils/calculations/breakawayCalculations";
import { selectors } from "../../../store/selectors/shipBuilderSelectors";
import { selectBreakawaySections, selectTotalTonnage } from "../../../store/selectors/breakawayHullSelectors";
import { setSectionPowerPlant } from "../../../store/features/breakawayHullSlice";

interface PowerDistribution {
  [sectionId: string]: {
    percentage: number;
    tons: number;
    power: number;
  };
}

/**
 * Breakaway Power Plant Step
 * Allows users to select a power plant for the entire breakaway hull system
 * and distribute it across sections proportionally or manually
 */
export const BreakawayPowerPlantStep: React.FC = () => {
  const dispatch = useAppDispatch();
  

  const bridge = useAppSelector(selectors.selectBridge);
  const sensors = useAppSelector(selectors.selectSensors);
  const weaponMounts = useAppSelector((state) => state.shipBuilder.weaponMounts);
  const defensiveSystems = useAppSelector(selectors.selectDefensiveSystems);
  const optionalSystemsPower = useAppSelector((state) => {
    const allOptionalSystems = [
      ...(Array.isArray(state.optionalSystems.structure) ? state.optionalSystems.structure : []),
      ...(Array.isArray(state.optionalSystems.modularHull) ? state.optionalSystems.modularHull : []),
      ...(Array.isArray(state.optionalSystems.power) ? state.optionalSystems.power : []),
      ...(Array.isArray(state.optionalSystems.drives) ? state.optionalSystems.drives : []),
      ...(Array.isArray(state.optionalSystems.fuel) ? state.optionalSystems.fuel : []),
      ...(Array.isArray(state.optionalSystems.accommodations) ? state.optionalSystems.accommodations : []),
      ...(Array.isArray(state.optionalSystems.bridge) ? state.optionalSystems.bridge : []),
      ...(Array.isArray(state.optionalSystems.cargo) ? state.optionalSystems.cargo : []),
      ...(Array.isArray(state.optionalSystems.drones) ? state.optionalSystems.drones : []),
      ...(Array.isArray(state.optionalSystems.sensors) ? state.optionalSystems.sensors : []),
      ...(Array.isArray(state.optionalSystems.external) ? state.optionalSystems.external : []),
      ...(Array.isArray(state.optionalSystems.internal) ? state.optionalSystems.internal : []),
      ...(Array.isArray(state.optionalSystems.software) ? state.optionalSystems.software : []),
      ...(Array.isArray(state.optionalSystems.commonAreas) ? state.optionalSystems.commonAreas : []),
    ];
    return allOptionalSystems.reduce((total: number, system) => {
      if (system.type === "modularHull" && Array.isArray(system.modules)) {
        const fitoutPower = system.modules
          .filter((m) => m.installed)
          .reduce((sum: number, m) => sum + (m.fitoutPower || 0), 0);
        return total + fitoutPower;
      }
      return total + (system.power || 0);
    }, 0);
  });
  const stateroomConfig = useAppSelector(selectors.selectStateroomConfig);

  // Get breakaway hull data
  const sections = useAppSelector(selectBreakawaySections);
  const totalTonnage = useAppSelector(selectTotalTonnage);

  // State for power plant configuration
  const [powerPlantTons, setPowerPlantTons] = useState<number>(1);
  const [useManualQuantity, setUseManualQuantity] = useState(false);
  const [distributionMode, setDistributionMode] = useState<'auto' | 'manual'>('auto');
  const [powerDistribution, setPowerDistribution] = useState<PowerDistribution>({});
  
  // Get selected type from Redux state (like main PowerPlantSelector)
  const selectedType: PowerPlantType = sections.length > 0 && sections[0]?.powerPlant?.type 
    ? sections[0].powerPlant.type 
    : "fusion";

  // Calculate power requirements for the entire ship
  const powerRequirements = useMemo(() => {
    // Calculate additional power from all systems
    const bridgePower = bridge?.power || 0;
    const sensorsPower = sensors?.power || 0;
    const weaponsPower = (weaponMounts || []).reduce(
      (total, mount) => total + (mount.power || 0),
      0,
    );
    const defensiveSystemsPower = (defensiveSystems || []).reduce(
      (total: number, system) => total + (system.power || 0),
      0,
    );
    
    // Calculate staterooms power
    const stateroomsPower = (() => {
      if (!stateroomConfig) return 0;
      return (
        (stateroomConfig.single || 0) * 0 +
        (stateroomConfig.double || 0) * 0 +
        Math.ceil((stateroomConfig.lowBerths?.count || 0) / 10) +
        (stateroomConfig.emergencyLowBerths?.count || 0) * 1 +
        (stateroomConfig.commonArea || 0) * 0.5
      );
    })();

    const additionalPowerRequirements = {
      bridgePower,
      sensorsPower,
      weaponsPower,
      defensiveSystemsPower,
      optionalSystemsPower,
      stateroomsPower,
    };

    // Use breakaway-specific power calculation
    return calculateBreakawayPowerRequirements(
      totalTonnage,
      sections,
      additionalPowerRequirements
    );
  }, [
    totalTonnage,
    sections,
    bridge,
    sensors,
    weaponMounts,
    defensiveSystems,
    optionalSystemsPower,
    stateroomConfig,
  ]);

  // Calculate power plant specifications
  const selectedSpec = POWER_PLANTS[selectedType];
  const autoPowerPlantTons = Math.ceil(
    powerRequirements.normalOperations / selectedSpec.powerPerTon,
  );
  const effectivePowerPlantTons = useManualQuantity ? powerPlantTons : autoPowerPlantTons;
  const powerPlantCost = effectivePowerPlantTons * selectedSpec.costPerTon;
  const powerPlantPower = effectivePowerPlantTons * selectedSpec.powerPerTon;
  const maintenanceCost = effectivePowerPlantTons * selectedSpec.maintenanceCost;

  // Calculate automatic distribution based on section sizes
  const autoDistribution = useMemo(() => {
    if (sections.length === 0) return {};

    const distribution: PowerDistribution = {};
    sections.forEach(section => {
      const sectionPercentage = Math.round((section.size / totalTonnage) * 100 * 10) / 10; // Round to 1 decimal place
      const sectionTons = Math.round((effectivePowerPlantTons * section.size) / totalTonnage);
      const sectionPower = Math.round((powerPlantPower * section.size) / totalTonnage);

      distribution[section.id] = {
        percentage: sectionPercentage,
        tons: sectionTons,
        power: sectionPower,
      };
    });

    return distribution;
  }, [sections, totalTonnage, effectivePowerPlantTons, powerPlantPower]);

  // Initialize distribution
  useEffect(() => {
    if (distributionMode === 'auto') {
      setPowerDistribution(autoDistribution);
    }
  }, [distributionMode, autoDistribution]);

  // Calculate distribution totals
  const distributionTotals = useMemo(() => {
    const totalPercentage = Object.values(powerDistribution).reduce((sum, dist) => sum + dist.percentage, 0);
    const totalTons = Object.values(powerDistribution).reduce((sum, dist) => sum + dist.tons, 0);
    const totalPower = Object.values(powerDistribution).reduce((sum, dist) => sum + dist.power, 0);

    // More lenient validation to account for rounding effects
    const isValid = Math.abs(totalPercentage - 100) <= 0.1; // Allow up to 0.1% deviation

    return {
      totalPercentage,
      totalTons,
      totalPower,
      isOverAllocated: totalPercentage > 100.1,
      isUnderAllocated: totalPercentage < 99.9,
      isValid,
    };
  }, [powerDistribution]);


  // Handle power plant type change
  const handleTypeChange = (type: PowerPlantType) => {
    console.log('BreakawayPowerPlantStep: handleTypeChange called with type:', type);
    const newSpec = POWER_PLANTS[type];
    const newAutoTons = Math.ceil(powerRequirements.normalOperations / newSpec.powerPerTon);
    
    if (!useManualQuantity) {
      setPowerPlantTons(newAutoTons);
    }
    
    // Immediately update Redux state for all sections (like main PowerPlantSelector)
    console.log('BreakawayPowerPlantStep: Immediately updating Redux state');
    sections.forEach(section => {
      const distribution = powerDistribution[section.id] || autoDistribution[section.id];
      if (distribution) {
        const roundedTons = Math.round(distribution.tons);
        const roundedPower = Math.round(distribution.power);
        
        const sectionPowerPlant: PowerPlant = {
          type: type,
          rating: 1,
          cost: roundedTons * newSpec.costPerTon * 1000000,
          weight: roundedTons,
          power: roundedPower,
          techLevel: newSpec.techLevel,
          maintenanceCost: roundedTons * newSpec.maintenanceCost * 1000000,
          description: `${newSpec.name} - ${formatNumber(roundedPower)} Power`,
        };

        dispatch(setSectionPowerPlant({ sectionId: section.id, powerPlant: sectionPowerPlant }));
      }
    });
  };

  // Handle power plant size change
  const handleQuantityChange = (quantity: number) => {
    setPowerPlantTons(quantity);
  };

  // Handle distribution mode change
  const handleDistributionModeChange = (mode: 'auto' | 'manual') => {
    setDistributionMode(mode);
    if (mode === 'auto') {
      setPowerDistribution(autoDistribution);
    }
  };

  // Handle manual distribution change
  const handleDistributionChange = (sectionId: string, field: 'percentage' | 'tons', value: number) => {
    if (distributionMode !== 'manual') return;

    const newDistribution = { ...powerDistribution };
    const section = newDistribution[sectionId];
    
    if (field === 'percentage') {
      section.percentage = Math.max(0, Math.min(100, value));
      section.tons = Math.round((effectivePowerPlantTons * section.percentage) / 100);
      section.power = Math.round((powerPlantPower * section.percentage) / 100);
    } else if (field === 'tons') {
      section.tons = Math.max(0, Math.min(effectivePowerPlantTons, Math.round(value)));
      section.percentage = Math.round((section.tons / effectivePowerPlantTons) * 100 * 10) / 10; // Round to 1 decimal place
      section.power = Math.round((powerPlantPower * section.percentage) / 100);
    }

    setPowerDistribution(newDistribution);
  };

  // Initialize power plant tons from existing sections (like main PowerPlantSelector)
  useEffect(() => {
    if (sections.length > 0) {
      const totalExistingTons = sections.reduce((total, section) => {
        return total + (section.powerPlant?.weight || 0);
      }, 0);
      
      if (totalExistingTons > 0) {
        setPowerPlantTons(totalExistingTons);
      }
      
      // Initialize power distribution from existing section power plants
      const existingDistribution: PowerDistribution = {};
      sections.forEach(section => {
        if (section.powerPlant) {
          const sectionPercentage = Math.round((section.size / totalTonnage) * 100 * 10) / 10;
          existingDistribution[section.id] = {
            percentage: sectionPercentage,
            tons: section.powerPlant.weight,
            power: section.powerPlant.power,
          };
        }
      });
      
      if (Object.keys(existingDistribution).length > 0) {
        setPowerDistribution(existingDistribution);
      }
    }
  }, [sections, totalTonnage]);

  // Auto-update manual quantity when auto calculation changes
  useEffect(() => {
    if (!useManualQuantity) {
      setPowerPlantTons(autoPowerPlantTons);
    }
  }, [autoPowerPlantTons, useManualQuantity]);

  if (sections.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No breakaway sections configured. Please configure breakaway hulls first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
          Breakaway Hull Power Plant Configuration
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Configure power plant for the entire system and distribute across {sections.length} sections
        </p>
      </div>

      {/* Power Plant Selection */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Power Plant Selection</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Power Plant Type
            </label>
            <select
              value={selectedType}
              onChange={(e) => handleTypeChange(e.target.value as PowerPlantType)}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
            >
              {Object.entries(POWER_PLANTS).map(([key, plant]) => (
                <option key={key} value={key}>
                  {plant.name} (TL {plant.techLevel})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Power Plant Size (tons)
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="1"
                value={effectivePowerPlantTons}
                onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              />
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={useManualQuantity}
                  onChange={(e) => setUseManualQuantity(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-600 dark:text-gray-300">Manual</span>
              </label>
            </div>
            {!useManualQuantity && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Auto-calculated based on power requirements
              </p>
            )}
          </div>
        </div>

        {/* Power Requirements Display */}
        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md mb-6">
          <h4 className="font-medium mb-3 text-gray-900 dark:text-gray-100">Total System Power Requirements</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Normal Operations:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100">{formatNumber(powerRequirements.normalOperations)} Power</span>
            </div>
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Jump Operations:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100">{formatNumber(powerRequirements.jumpOperations)} Power</span>
            </div>
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Maximum Load:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100">{formatNumber(powerRequirements.maximumLoad)} Power</span>
            </div>
          </div>
        </div>

        {/* Power Plant Details */}
        <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-md">
          <h4 className="font-medium mb-3 text-gray-900 dark:text-gray-100">Selected Power Plant Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Total Power Output:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100">{formatNumber(powerPlantPower)} Power</span>
            </div>
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Total Cost:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100">{formatCurrency(powerPlantCost * 1000000)}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Total Maintenance:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100">{formatCurrency(maintenanceCost * 1000000)}/month</span>
            </div>
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Tech Level:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100">{selectedSpec.techLevel}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Power Distribution */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Power Distribution</h3>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="auto-distribution"
                name="distribution-mode"
                checked={distributionMode === 'auto'}
                onChange={() => handleDistributionModeChange('auto')}
                className="mr-2"
              />
              <label htmlFor="auto-distribution" className="text-sm text-gray-700 dark:text-gray-300">Auto (by size)</label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="manual-distribution"
                name="distribution-mode"
                checked={distributionMode === 'manual'}
                onChange={() => handleDistributionModeChange('manual')}
                className="mr-2"
              />
              <label htmlFor="manual-distribution" className="text-sm text-gray-700 dark:text-gray-300">Manual</label>
            </div>
          </div>
        </div>

        {/* Distribution Status */}
        <div className={`p-3 rounded-md mb-4 ${
          distributionTotals.isValid 
            ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700' 
            : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700'
        }`}>
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Distribution Status: </span>
              <span className={distributionTotals.isValid ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
                {distributionTotals.isValid ? 'Valid' : 'Invalid'}
              </span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Total: </span>
              <span className={distributionTotals.isValid ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
                {distributionTotals.totalPercentage.toFixed(1)}%
              </span>
            </div>
          </div>
          {!distributionTotals.isValid && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              {distributionTotals.isOverAllocated 
                ? `Over-allocated by ${(distributionTotals.totalPercentage - 100).toFixed(1)}%`
                : `Under-allocated by ${(100 - distributionTotals.totalPercentage).toFixed(1)}%`
              }
            </p>
          )}
        </div>

        {/* Section Distribution Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700">
                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Section</th>
                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Size (tons)</th>
                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Size %</th>
                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Power %</th>
                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Power Tons</th>
                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Power Output</th>
              </tr>
            </thead>
            <tbody>
              {sections.map(section => {
                const distribution = powerDistribution[section.id];
                const sizePercentage = (section.size / totalTonnage) * 100;
                
                return (
                  <tr key={section.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {section.name}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                      {formatNumber(section.size)}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                      {sizePercentage.toFixed(1)}%
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                      {distributionMode === 'manual' ? (
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={distribution?.percentage.toFixed(1) || '0'}
                          onChange={(e) => handleDistributionChange(section.id, 'percentage', parseFloat(e.target.value) || 0)}
                          className="w-20 p-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-gray-100"
                        />
                      ) : (
                        <span className="text-sm text-gray-900 dark:text-gray-100">{distribution?.percentage.toFixed(1)}%</span>
                      )}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                      {distributionMode === 'manual' ? (
                        <input
                          type="number"
                          min="0"
                          max={effectivePowerPlantTons}
                          step="1"
                          value={Math.round(distribution?.tons || 0)}
                          onChange={(e) => handleDistributionChange(section.id, 'tons', parseInt(e.target.value) || 0)}
                          className="w-20 p-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-gray-100"
                        />
                      ) : (
                        <span className="text-sm text-gray-900 dark:text-gray-100">{formatNumber(Math.round(distribution?.tons || 0))}</span>
                      )}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatNumber(Math.round(distribution?.power || 0))} Power
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-gray-100 dark:bg-gray-600 font-medium">
                <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100">Total</td>
                <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100">{formatNumber(totalTonnage)}</td>
                <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100">100.0%</td>
                <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100">{distributionTotals.totalPercentage.toFixed(1)}%</td>
                <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100">{formatNumber(Math.round(distributionTotals.totalTons))}</td>
                <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100">{formatNumber(Math.round(distributionTotals.totalPower))} Power</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Configuration automatically applied when changes are made */}
        {!distributionTotals.isValid && (
          <div className="mt-6 text-center">
            <p className="text-sm text-yellow-600 dark:text-yellow-400">
              Power distribution is {distributionTotals.isOverAllocated ? 'over' : 'under'} allocated ({distributionTotals.totalPercentage.toFixed(1)}%)
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
